// import i18next from 'i18next';
import { Box } from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

import { slugify } from '../../utilities/slugify';
import { DEFAULT_MARGIN_TEXT } from '../Spacer/Spacer';
import { Typography } from '../Typography/Typography';
import type { CustomVariant } from '../Typography/Typography.types';

export interface RichTextRendererProps {
  text: string;
  skipHtml?: boolean | undefined;
  components?: unknown;
  renderLink?(link, children): React.ReactNode;
  renderImage?(src, alt): React.ReactNode;
  textColor?: 'inherit' | 'text.primary' | 'text.secondary' | 'primary.contrastText';
  innerPadding?: 'vertical' | 'horizontal' | 'both' | 'none';
  textVariant?: 'body1' | 'body2' | 'c1';
  component?: CustomVariant;
}

export type Fragments = {
  text?: TextFragment[];
};

export type TextFragment = {
  range: [number, number];
  element: string;
  // TODO remove
  attributes?: {
    'data-json': string;
    style: string;
  };
  value?: string;
};

export type CleanDataInput = {
  html: {
    blocks: {
      type: string;
      data: {
        text: string;
        tag: CustomVariant;
        level?: number;
        items?: [];
        code?: string;
        file?: {
          url: string;
          caption: string;
        };
        caption?: string;
        style?: 'ordered';
        html?: string;
      };
      fragments?: Fragments;
    }[];
  };
};

const parseStyle = (style: string): { [key: string]: string } => {
  if (!style) {
    return {};
  }
  const whiteList = ['white-space'];
  return style.split(';').reduce((acc, rule) => {
    const [key, value] = rule.split(':');
    const attribute = key.trim();
    if (key && value && whiteList.includes(attribute)) {
      acc[attribute] = value.trim();
    }
    return acc;
  }, {});
};

const parseInline = (text: string, fragments: Fragments, renderLink: (link, children) => React.ReactNode) => {
  if (!fragments) {
    return text;
  }

  return (fragments?.text || []).map((fragment, index) => {
    switch (fragment.element) {
      case '#text':
        return <React.Fragment key={`text-${index}`}>{fragment.value}</React.Fragment>;
      case 'A':
        if (!fragment.attributes['data-json']) {
          console.group('Could not render Link:');
          console.info(fragment);
          console.groupEnd();
          return fragment.value;
        }

        // TODO remove data-json
        return (
          <React.Fragment key={`text-${index}`}>
            {renderLink(JSON.parse(fragment.attributes['data-json']), fragment.value)}
          </React.Fragment>
        );
      case 'B':
        return (
          <Typography key={`text-${index}`} variant="inherit" component="strong" color="text.primary" fontWeight="bold">
            {fragment.value}
          </Typography>
        );
      case 'SUP':
        return <sup key={`text-${index}`}>{fragment.value}</sup>;
      case 'CODE':
        return (
          <Typography variant="body1" component="code">
            {fragment.value}
          </Typography>
        );
      case 'BR':
        return <br key={`text-${index}`} />;
      case 'SPAN':
        if (!fragment.attributes?.style) {
          return <React.Fragment key={`text-${index}`}>{fragment.value}</React.Fragment>;
        }
        return (
          <Box key={`text-${index}`} component="span" sx={parseStyle(fragment?.attributes?.style)}>
            {fragment.value}
          </Box>
        );
      default:
        return <React.Fragment key={`text-${index}`}>{fragment.value}</React.Fragment>;
    }
  });
};
const renderEditorJSContent = (text, renderLink, renderImage, textColor, textVariant, innerPadding, component) => {
  return (
    <>
      {JSON.parse(text).blocks.map((block, index) => {
        switch (block.type) {
          case 'paragraph':
            return (
              <Typography
                key={index}
                variant={textVariant || block.data.variant}
                component={component || block.data.tag}
                color={textColor ? textColor : block.data.variant?.includes('body') ? 'text.secondary' : null}
                innerPadding={innerPadding || 'both'}
                id={slugify(block.data.text)}
              >
                {parseInline(block.data.text, block.fragments, renderLink)}
              </Typography>
            );
          case 'image':
            return block.data?.file?.url && renderImage(block.data.file.url, block.data.file.caption);

          case 'delimiter':
            return <hr key={index} />;

          case 'list':
            if (!Array.isArray(block.data.items)) {
              return null;
            }

            return (
              <Typography
                component={block.data.style === 'ordered' ? 'ol' : 'ul'}
                innerPadding={innerPadding || 'both'}
                padding={0}
                paddingLeft={DEFAULT_MARGIN_TEXT}
                color={textColor || 'text.secondary'}
                key={`list-${index}`}
                display="flex"
                rowGap={DEFAULT_MARGIN_TEXT}
                flexDirection="column"
              >
                {block.data.items.map((li: string, idx: number) => {
                  return (
                    <Typography variant="body1" component="li" key={`list-${index}-${idx}-item`}>
                      {parseInline(li, block.fragments[idx], renderLink)}
                    </Typography>
                  );
                })}
              </Typography>
            );

          default:
            return null;
        }
      })}
    </>
  );
};

export const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  text,
  components,
  textColor,
  textVariant,
  innerPadding,
  skipHtml = false,
  component,
  renderLink = (link, children) => {
    return link ? <a href={link.url ? link.url : link.reference.slugPath}>{children}</a> : children;
  },
  renderImage = (src, alt) => (
    <img src={src} alt={alt} width={290} height={290} style={{ display: 'block', marginBottom: '5px' }} />
  ),
}: RichTextRendererProps) => {
  if (!text) {
    return null;
  }

  return text.includes('"blocks":') ? (
    renderEditorJSContent(text, renderLink, renderImage, textColor, textVariant, innerPadding, component)
  ) : (
    <ReactMarkdown remarkPlugins={[gfm]} components={components} skipHtml={skipHtml}>
      {text}
    </ReactMarkdown>
  );
};
